import { Row, RowContent, RowIcon, RowLabel, Rows } from "@shared/components/menus/DrilldownMenu"
import type { Planbook } from "@shared/models/planbook"
// File01 is the bare page, File02 the one with content lines — a blank template and a
// written one, told apart by the glyph rather than by the label alone.
import { tracked } from "@supergrain/kernel/react"
import { Check, File01, File02, LayoutGrid01 } from "@untitledui/icons"
import type { ComponentType, ReactNode } from "react"
import { ThreeDotSpinner } from "react-app/components/util/ThreeDotSpinner"
import type { TemplateChoice } from "./template-choices"
import { BLANK_TEMPLATE_ID, useTemplateChoices } from "./template-choices"

/**
 * Untitled UI draws at strokeWidth 2 on a 24 viewBox, which lands at 1.33px in a 16px row
 * icon — half again as heavy as the hand-drawn icons beside it in these menus (0.89px, being
 * strokeWidth 1 on an 18 viewBox). Redraw at the weight the rest of the menu uses.
 */
function atHouseWeight(
  Icon: ComponentType<{ className?: string; strokeWidth?: number }>,
): ComponentType<{ className?: string }> {
  return function HouseWeightIcon({ className }) {
    return <Icon className={className} strokeWidth={1.35} />
  }
}

const GalleryIcon = atHouseWeight(LayoutGrid01)
const BlankTemplateIcon = atHouseWeight(File01)
const TemplateIcon = atHouseWeight(File02)
/**
 * The row the list is already set to. The check takes the icon's place rather than sitting at the
 * far end of the row: a mark in the margin is something you have to go looking for, and the one
 * row a scan has to land on is this one. The label goes bold with it — a check beside type set
 * exactly like every other row's reads as decoration rather than as state.
 */
const CurrentIcon = atHouseWeight(Check)
const CURRENT_LABEL = "font-bold text-black"

interface Props {
  planbook: Planbook
  /** Pastes the template over this lesson or unit (the blank row passes BLANK_TEMPLATE_ID). */
  onChoose: (templateId: string) => void
  /** Leaves the menu for the full template-library modal. */
  onChooseFromGallery: () => void
  /**
   * Shown above the choices, for a caller whose version of this list does something other than
   * the obvious. The rows are identical either way, so anything that changes what pressing one
   * means has to be said here.
   */
  note?: ReactNode
  /**
   * The template this list is already set to, marked with a check. Null when there is nothing to
   * mark — a lesson being replaced was not made "from" anything the list still knows about, and a
   * class whose days were set individually has no one default to point at.
   */
  currentTemplateId?: string | null
}

/**
 * The "Replace with Template" drilldown: the two ways to get a template that isn't listed,
 * then every template already within reach, grouped by whose it is — the planbook's own first,
 * then each group's. One surface, so arrow keys and typeahead cross the group names.
 *
 * Loading the templates belongs here rather than in the menu: a drilldown body is built only
 * once the user opens it, and a menu renders per lesson — asking every lesson on the planner
 * for the whole group-template chain up front would be a page-load cost nobody asked for.
 */
export const ReplaceWithTemplateView = tracked(function ReplaceWithTemplateView({
  planbook,
  onChoose,
  onChooseFromGallery,
  note,
  currentTemplateId = null,
}: Props) {
  const { mine, shared, isLoading, hasError } = useTemplateChoices(planbook)
  // The planbook's own templates are just another named group: whose templates these are is
  // the only thing the list has to say, and saying it twice — a category, then the name —
  // buried the names that actually tell them apart.
  const groups: Array<{ key: string; label: string; templates: Array<TemplateChoice> }> = [
    ...(mine.length > 0 ? [{ key: "mine", label: "My Templates", templates: mine }] : []),
    ...shared.map((group) => ({
      key: group.groupId,
      label: group.groupTitle,
      templates: group.templates,
    })),
  ]

  // The spinner only while there is nothing to show. A straggling group — one whose
  // documents are slow or never arrive — must not leave a spinner hanging under a list
  // that already reads as complete.
  const emptyState = () => {
    if (groups.length > 0) return null
    if (isLoading) {
      return (
        <div className="flex justify-center py-4">
          <ThreeDotSpinner />
        </div>
      )
    }
    if (hasError) return null
    return (
      <p className="mt-3 text-sm text-gray-500">
        No templates yet — choose one from the gallery to get started.
      </p>
    )
  }

  return (
    // pb-3 matches MenuRoot's, which a drilled-in view does not get: the root menu's own
    // bottom padding comes from that wrapper, and nothing stands in for it here.
    <Rows className="px-5 pb-3 [--menu-row-inset:0px]">
      {note}
      <Row typeaheadText="Choose from Gallery" onAction={onChooseFromGallery}>
        <RowContent>
          <RowIcon icon={GalleryIcon} />
          <RowLabel>Choose from Gallery</RowLabel>
        </RowContent>
      </Row>
      <Row typeaheadText="Use a Blank Template" onAction={() => onChoose(BLANK_TEMPLATE_ID)}>
        <RowContent>
          <RowIcon
            icon={currentTemplateId === BLANK_TEMPLATE_ID ? CurrentIcon : BlankTemplateIcon}
          />
          <RowLabel className={currentTemplateId === BLANK_TEMPLATE_ID ? CURRENT_LABEL : undefined}>
            Use a Blank Template
          </RowLabel>
        </RowContent>
      </Row>

      {groups.map((group) => (
        <TemplateGroup
          key={group.key}
          group={group}
          onChoose={onChoose}
          currentTemplateId={currentTemplateId}
        />
      ))}

      {emptyState()}

      {/* A list that is short by an unknown amount reads exactly like a complete one, so say
          so — under a full list as much as under an empty one. */}
      {hasError ? (
        <p data-test="template load error" className="mt-3 text-sm text-gray-500">
          Some templates couldn’t be loaded.
        </p>
      ) : null}
    </Rows>
  )
})

/**
 * Whose templates these are, then the templates.
 *
 * The label wears the Add Lesson menu's section-header treatment: sentence case, bold, and
 * larger than the rows beneath it, with space rather than a rule doing the separating. A
 * heading there outweighs its rows by going heavier and darker; these rows are already bold
 * black, so here the same relationship has to be bought with size.
 *
 * Heading and rows share one left edge — indenting on top of a heading this distinct only buys
 * a ragged margin.
 */
function TemplateGroup({
  group,
  onChoose,
  currentTemplateId,
}: {
  group: { label: string; templates: Array<TemplateChoice> }
  onChoose: (templateId: string) => void
  currentTemplateId: string | null
}) {
  return (
    <>
      <h4 className="mt-4 mb-1 text-lg leading-snug font-bold text-black">{group.label}</h4>
      {group.templates.map((template) => (
        <Row
          key={template.id}
          typeaheadText={template.title}
          onAction={() => onChoose(template.id)}
        >
          <RowContent>
            <RowIcon icon={template.id === currentTemplateId ? CurrentIcon : TemplateIcon} />
            {/* Regular weight: the row is one of many choices to scan, and the heading above
                already carries the emphasis. The one row that is already the answer is the
                exception — it has to win the scan. */}
            <RowLabel className={template.id === currentTemplateId ? CURRENT_LABEL : "font-normal"}>
              {template.title}
            </RowLabel>
          </RowContent>
        </Row>
      ))}
    </>
  )
}
